/**
 * Tira Pa More — design tokens (deliverable D2).
 *
 * Source of truth for palette, type scale, spacing, radius, elevation and
 * motion. Lifted from the Claude Design component sheet (Turn 1) and the
 * design spec §5.1 / §6 / §7. Exported as plain data so the web client, and
 * later the Godot client's theme resource, read the same numbers.
 */
/** Palette — design spec §5.1. Venue accents are identity threads, never fills. */
export const palette = {
    /** Night base — backgrounds, chrome. */
    gabi: '#171C26',
    /** Wood surfaces, panels. */
    kahoy: '#4A3221',
    /** Paper material — sheets, cards UI, receipts. */
    manila: '#E8D9B0',
    /** Brand metal — chips, wins, dama caps, accents. */
    tansan: '#C9A227',
    /** Alerts, timers, Tongits venue accent. */
    halina: '#E4572E',
    /** Mahjong venue accent. */
    jade: '#2FA36B',
    /** Pusoy Dos venue accent. */
    kuryente: '#2D7DD2',
    /** Pusoy venue accent. */
    lawa: '#1F8A8A',
    /** Gera venue accent. */
    oliba: '#6E7F3E',
};
/**
 * Derived surface ramp. The design sheet layers the night base rather than
 * using flat #171C26 everywhere — these are the exact values it uses.
 */
export const surface = {
    /** Deepest — page background behind the compound. */
    void: '#0C1017',
    /** Panel gradient top. */
    raised: '#0F1420',
    /** Panel body. */
    base: palette.gabi,
    /** Panel gradient bottom / hovered rows. */
    lifted: '#1B212D',
    /** Hairline dividers over dark. */
    hairline: 'rgba(255,255,255,.08)',
    /** Card border over dark. */
    border: 'rgba(232,217,176,.12)',
};
/** Text ramp — manila at descending opacity, per the component sheet. */
export const ink = {
    primary: palette.manila,
    secondary: 'rgba(232,217,176,.75)',
    muted: 'rgba(232,217,176,.55)',
    faint: 'rgba(232,217,176,.45)',
    /** On tansan fills. */
    onGold: '#241B07',
    /** On manila (receipt) surfaces. */
    onPaper: '#241B07',
};
/** Two-source practical lighting — design spec §5. Used for glows and gradients. */
export const light = {
    /** Warm tungsten key. */
    tungsten: '#FFB35C',
    /** Cool fluorescent / moonlight fill. */
    fluorescent: '#CDE8FF',
};
/** Per-venue accent lookup. Keys match GameId in @tira/protocol. */
export const venueAccent = {
    mahjong: palette.jade,
    tongits: palette.halina,
    pusoydos: palette.kuryente,
    pusoy: palette.lawa,
    dama: palette.tansan,
    gera: palette.oliba,
};
/**
 * Chip denominations — design spec §7.1. Casino-standard edge colours for
 * instant reads. No currency symbols anywhere, by hard rule.
 */
export const chipDenominations = [
    { value: 1, name: 'puti', ring: '#E9E7E0', inner: '#F7F6F2', text: '#241B07' },
    { value: 5, name: 'pula', ring: '#B0342A', inner: '#C9433A', text: '#FFF3EC' },
    { value: 25, name: 'berde', ring: '#1F7A4D', inner: '#2FA36B', text: '#F0FFF6' },
    { value: 100, name: 'itim', ring: '#14161C', inner: '#22262F', text: '#E8D9B0' },
    { value: 500, name: 'lila', ring: '#4C2C6B', inner: '#67418C', text: '#F3E9FF' },
    { value: 1000, name: 'ginto', ring: '#26200C', inner: '#C9A227', text: '#241B07' },
];
/** Typography — design spec §6. Manrope for UI; never default to Inter. */
export const type = {
    family: {
        /** Jeepney-letras energy. Comp stand-in until the custom wordmark lands. */
        display: "'Alfa Slab One', Georgia, serif",
        /** UI. */
        ui: "'Manrope', system-ui, -apple-system, sans-serif",
        /** Numerals — chip counts, pots, timers. Tabular figures. */
        data: "'Archivo', system-ui, sans-serif",
        /** Tile glyphs. */
        glyph: "'Noto Serif TC', 'Songti SC', serif",
    },
    /** Sizes in px at the 6.1" landscape reference. */
    scale: {
        display: 54,
        title: 26,
        heading: 18,
        body: 14,
        caption: 11.5,
        micro: 9.5,
        data: 20,
    },
    weight: { regular: 400, medium: 500, semibold: 600, bold: 700, heavy: 800 },
    /** Numbers are first-class: always tabular so counts don't jitter. */
    tabular: "'tnum' 1",
};
/** 4pt base spacing scale. */
export const space = { xs: 4, sm: 8, md: 12, lg: 18, xl: 24, xxl: 34 };
export const radius = { sm: 6, md: 9, lg: 14, xl: 22, pill: 999 };
export const elevation = {
    card: '0 5px 10px rgba(0,0,0,.5)',
    chip: '0 7px 14px rgba(0,0,0,.5), inset 0 0 0 1px rgba(0,0,0,.28)',
    sheet: '0 -8px 30px rgba(0,0,0,.55)',
    lamp: '0 0 40px rgba(255,179,92,.18)',
};
/**
 * Motion. Reduced-motion swaps physics flourishes for fades (design spec §5.2)
 * — consumers must honour `prefers-reduced-motion`, so these are the *upper*
 * bounds, not fixed values.
 */
export const motion = {
    instant: 90,
    quick: 160,
    base: 240,
    deliberate: 420,
    /** Signature moment push-ins (Todas slam, Fight, reveal cascade). */
    signature: 900,
    easing: {
        standard: 'cubic-bezier(.2,.8,.2,1)',
        enter: 'cubic-bezier(.05,.7,.1,1)',
        exit: 'cubic-bezier(.3,0,.8,.15)',
        /** Physical settle — chip stacks, tile clack, cap flip. */
        settle: 'cubic-bezier(.34,1.56,.64,1)',
    },
};
/** Minimum touch target — design spec §14 (48dp). */
export const layout = {
    minTouch: 48,
    /** Landscape reference viewport. */
    ref: { w: 844, h: 390 },
    safeInset: 16,
};
/**
 * Emits the token set as CSS custom properties for the web client.
 * Kept here so there is exactly one place a colour is written down.
 */
export function cssVariables() {
    const lines = [];
    for (const [k, v] of Object.entries(palette))
        lines.push(`--c-${k}: ${v};`);
    for (const [k, v] of Object.entries(surface))
        lines.push(`--s-${k}: ${v};`);
    for (const [k, v] of Object.entries(ink))
        lines.push(`--ink-${k}: ${v};`);
    for (const [k, v] of Object.entries(light))
        lines.push(`--light-${k}: ${v};`);
    for (const [k, v] of Object.entries(venueAccent))
        lines.push(`--venue-${k}: ${v};`);
    for (const [k, v] of Object.entries(type.family))
        lines.push(`--font-${k}: ${v};`);
    for (const [k, v] of Object.entries(type.scale))
        lines.push(`--fs-${k}: ${v}px;`);
    for (const [k, v] of Object.entries(space))
        lines.push(`--sp-${k}: ${v}px;`);
    for (const [k, v] of Object.entries(radius))
        lines.push(`--r-${k}: ${v}px;`);
    for (const [k, v] of Object.entries(elevation))
        lines.push(`--el-${k}: ${v};`);
    for (const [k, v] of Object.entries(motion.easing))
        lines.push(`--ease-${k}: ${v};`);
    for (const [k, v] of Object.entries(motion)) {
        if (typeof v === 'number')
            lines.push(`--dur-${k}: ${v}ms;`);
    }
    return `:root {\n  ${lines.join('\n  ')}\n}`;
}
//# sourceMappingURL=index.js.map